import { useCallback, useEffect, useRef, useState } from 'react'

import { Button, Dialog, Field, Input, Portal, useDisclosure } from '@chakra-ui/react'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import {
  resetCreateBlueprint,
  setSelectedTool,
  updateSelectedBlueprint,
} from '@editor/pages/projects/blueprint-editor/store/blueprintEditor'
import { RootState, useAppDispatch } from '@editor/store/app'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'

const BlueprintFormModal = () => {
  // Hooks
  const { open, onOpen, onClose } = useDisclosure()

  // Refs
  const inputRef = useRef<HTMLInputElement>(null)

  // States
  const [errorMessage, setErrorMessage] = useState('')

  // Store
  const dispatch = useAppDispatch()
  const isCreatingBlueprint = useSelector((state: RootState) => state.blueprint.editor.isCreatingBlueprint)
  const blueprints = useSelector((state: RootState) => state.blueprint.editor.blueprints)

  /**
   * Checks if the blueprint name is a duplicate.
   *
   * @param name - The name of the blueprint to check.
   */
  const isDuplicateName = useCallback(
    (name: string) => blueprints.some((blueprint) => blueprint.name === name),
    [blueprints],
  )

  /**
   * Checks if the blueprint name is empty.
   */
  const validateName = useCallback(() => {
    if (!inputRef.current?.value) {
      setErrorMessage('名前を入力してください。')
      return false
    }

    if (inputRef.current && isDuplicateName(inputRef.current.value)) {
      setErrorMessage('同じ名前の設計図面が既に存在します。')
      return false
    }

    setErrorMessage('')
    return true
  }, [isDuplicateName])

  /**
   * Handles the submission of the blueprint form.
   */
  const handleSubmit = () => {
    if (validateName() === false) {
      return
    }

    // Track blueprint creation
    mixpanel.track('Blueprint Operation', {
      Operation: 'create',
      Name: inputRef.current?.value,
    })

    dispatch(setSelectedTool(BlueprintEditorTools.BlueprintManager))
    setErrorMessage('')
    onClose()
  }

  const handleCancel = () => {
    setErrorMessage('')
    onClose()
    dispatch(resetCreateBlueprint())
  }

  /**
   * Open modal when creating a new blueprint.
   */
  useEffect(() => {
    if (isCreatingBlueprint) {
      onOpen()
      setTimeout(() => {
        inputRef.current?.focus()
      })
    }
  }, [isCreatingBlueprint, onOpen])

  return (
    <Dialog.Root
      closeOnInteractOutside={false}
      closeOnEscape={false}
      open={open}
      onOpenChange={(e) => {
        if (!e.open) onClose()
      }}
      trapFocus={false}
      size="md"
      placement="center"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header fontSize="xl" fontWeight="bold" color="gray.600">
              設計図面を作成
            </Dialog.Header>
            <Dialog.Body>
              <Field.Root invalid={errorMessage.length > 0} required>
                <Field.Label htmlFor="blueprint-name">
                  設計図面名
                  <Field.RequiredIndicator />
                </Field.Label>
                <Input
                  ref={inputRef}
                  id="blueprint-name"
                  type="text"
                  onChange={(e) => {
                    dispatch(updateSelectedBlueprint({ name: e.target.value }))
                    validateName()
                  }}
                  onKeyDown={(e) => {
                    // if Enter is pressed, submit the form
                    if (e.key === 'Enter') {
                      handleSubmit()
                    }
                  }}
                />
                <Field.ErrorText>{errorMessage}</Field.ErrorText>
              </Field.Root>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" marginEnd={3} py={2} minW="100px" onClick={handleCancel}>
                キャンセル
              </Button>

              <Button colorPalette="blue" minW="100px" onClick={handleSubmit} py={2} disabled={errorMessage.length > 0}>
                次へ
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default BlueprintFormModal
